'use client';
import { useState, useCallback } from 'react';
import { arrayOf } from '@s7/utils';
import { useQueryInternalContext } from '../../context/QueryInternalContext.js';
import { useQueryContext } from '../../context/QueryContext.js';
import { defaultResponseTransform } from '../utils/defaultResponseTransform.js';
import { isSingularIdent } from '../../cache/createCache/idents.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var useMutation = function(options) {
    var _ref = options || {}, handlers = _ref.handlers, others = _object_without_properties_loose(_ref, [
        "handlers"
    ]);
    var _ref1 = handlers || {}, onSuccess = _ref1.onSuccess, onError = _ref1.onError, onFinish = _ref1.onFinish;
    var _useQueryInternalContext = useQueryInternalContext(), bus = _useQueryInternalContext[0];
    var _useQueryContext = useQueryContext(others), client = _useQueryContext[0];
    var _useState = useState(false), loading = _useState[0], setLoading = _useState[1];
    var _useState1 = useState(null), error = _useState1[0], setError = _useState1[1];
    var _useState2 = useState(null), data = _useState2[0], setData = _useState2[1];
    var notifyQueries = function(data, invalidate) {
        if (isSingularIdent(invalidate)) {
            bus.emit(invalidate);
        } else {
            bus.emit(invalidate.key, invalidate.ident(data));
        }
    };
    var forceHiddenMutation = /*#__PURE__*/ _async_to_generator(function(options) {
        var query, _options_transform, transform, invalidate, _arrayOf, input, clientOptions, data, response, result, error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    query = options.query, _options_transform = options.transform, transform = _options_transform === void 0 ? defaultResponseTransform : _options_transform, invalidate = options.invalidate;
                    _arrayOf = arrayOf(query), input = _arrayOf[0], clientOptions = _arrayOf[1];
                    data = null;
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        4,
                        5,
                        6
                    ]);
                    return [
                        4,
                        client(input, clientOptions)
                    ];
                case 2:
                    response = _state.sent();
                    setError(null);
                    return [
                        4,
                        transform(response.clone())
                    ];
                case 3:
                    result = _state.sent();
                    data = result;
                    setData(result);
                    onSuccess == null ? void 0 : onSuccess(result);
                    if (invalidate) {
                        notifyQueries(result, invalidate);
                    }
                    return [
                        3,
                        6
                    ];
                case 4:
                    error = _state.sent();
                    onError == null ? void 0 : onError(error);
                    setError(error);
                    return [
                        3,
                        6
                    ];
                case 5:
                    onFinish == null ? void 0 : onFinish();
                    return [
                        7
                    ];
                case 6:
                    return [
                        2,
                        data
                    ];
            }
        });
    });
    var mutate = useCallback(/*#__PURE__*/ _async_to_generator(function(options) {
        var result;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    setLoading(true);
                    return [
                        4,
                        forceHiddenMutation(options)
                    ];
                case 1:
                    result = _state.sent();
                    setLoading(false);
                    return [
                        2,
                        result
                    ];
            }
        });
    }), []);
    var reset = useCallback(function() {
        setLoading(false);
        setError(null);
        setData(null);
    }, []);
    return [
        mutate,
        {
            data: data,
            loading: loading,
            error: error,
            reset: reset
        }
    ];
};

export { useMutation };
